import { z } from 'zod/v4';
import { CAPA_PROPAGATION_FIELDS } from '../../constants.js';

/**
 * Per-child facts that propagation needs and that the access-control data does
 * not carry: whether the action is archived, whether its due date is still
 * empty, and which companies already hold OWNER/CREATOR on it (those must never
 * be written as COLLABORATOR).
 */
export const ActionPropagationRowSchema = z.object({
  actionId: z.string(),
  isArchived: z.number(),
  dueDate: z.union([z.string(), z.date()]).nullable(),
  ownCompanyIds: z.array(z.string()),
});

export type ActionPropagationRow = z.infer<typeof ActionPropagationRowSchema>;

export const PlanPropagationFieldResultSchema = z.object({
  field: z.enum(CAPA_PROPAGATION_FIELDS),
  /** Children the field was written to. */
  appliedActionIds: z.array(z.string()),
  /** Children skipped because the actor may not edit this field on them. */
  skippedActionIds: z.array(z.string()),
  /** Children whose write threw. Recorded rather than aborting the run. */
  failedActionIds: z.array(z.string()),
});

export type PlanPropagationFieldResult = z.infer<
  typeof PlanPropagationFieldResultSchema
>;

export const PlanPropagationResultSchema = z.object({
  /** Non-archived children considered for propagation. */
  childActionCount: z.number(),
  /** Union across fields — at least one field was written. */
  appliedActionIds: z.array(z.string()),
  /** Children no field could be written to (no access, or every field denied). */
  skippedActionIds: z.array(z.string()),
  failedActionIds: z.array(z.string()),
  fields: z.array(PlanPropagationFieldResultSchema),
});

export type PlanPropagationResult = z.infer<typeof PlanPropagationResultSchema>;

/**
 * Query flag shared by every endpoint that can propagate to child actions.
 *
 * Deliberately lenient, unlike `strictBooleanQuerySchema` and the older
 * `includeLinkedActions`: propagation is opt-in, so anything that is not an
 * explicit `true` means "don't propagate" — a malformed flag must never 400 an
 * otherwise valid plan update. Absent, `''`, `'1'`, `'yes'` and a repeated
 * param all resolve to `false`.
 */
export const UpdateLinkedActionsQuerySchema = z.object({
  updateLinkedActions: z
    .unknown()
    .optional()
    .transform(val => val === true || val === 'true'),
});

export type UpdateLinkedActionsQuery = z.infer<
  typeof UpdateLinkedActionsQuerySchema
>;
